import React, { useState } from "react"
import PropTypes from "prop-types"
import { useTranslation } from "react-i18next"
import { Icon } from "ui/Icon"
import { BLACKOUT_FILTER_SECTIONS } from "../../constants"
import styles from "./index.module.css"

/**
 * The phone filter sheet: one collapsible section per filter, only one open at a time.
 *
 * Each collapsed header carries its own state — a dot and a summary of what it is filtering by,
 * plus a Reset that clears that section alone. Selections apply as they are made, so the footer's
 * primary action only dismisses the sheet.
 */
const FilterAccordion = ({ sections }) => {
  const { t } = useTranslation()
  const [expandedKey, setExpandedKey] = useState(null)

  return (
    <div className={styles.accordion}>
      {sections.map((section) => {
        const isExpanded = expandedKey === section.key
        return (
          <div key={section.key} className={styles.accordionRow}>
            <div className={styles.accordionHeaderRow}>
              <button
                type="button"
                className={styles.accordionHeader}
                aria-expanded={isExpanded}
                onClick={() => setExpandedKey(isExpanded ? null : section.key)}
              >
                <span className={styles.accordionTitle}>
                  {section.label}
                  {section.isActive && <span className={styles.activeDot} aria-hidden="true" />}
                </span>
                <span className={styles.accordionSummary}>{section.summary}</span>
              </button>

              {section.isActive && (
                <button type="button" className={styles.resetLink} onClick={section.onReset}>
                  {t("settings.blackout.reset", { defaultValue: "Reset" })}
                </button>
              )}

              <Icon
                name={isExpanded ? "ChevronUp" : "ChevronDown"}
                width={20}
                height={20}
                color="var(--color-text-secondary-dark)"
                className={styles.accordionChevron}
              />
            </div>

            {isExpanded && <div className={styles.accordionBody}>{section.panel}</div>}
          </div>
        )
      })}
    </div>
  )
}

FilterAccordion.propTypes = {
  sections: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.oneOf(Object.values(BLACKOUT_FILTER_SECTIONS)).isRequired,
      label: PropTypes.string.isRequired,
      /** What the section is filtering by, or the "everything" copy when it is untouched. */
      summary: PropTypes.string.isRequired,
      isActive: PropTypes.bool,
      onReset: PropTypes.func.isRequired,
      panel: PropTypes.node.isRequired
    })
  ).isRequired
}

export default FilterAccordion
